import React from 'react';
import styles from './style.module.scss';
import PageContainer from '../../Pages/PageContainer';
import OrganizationThemeSelector from '../../Components/AuditOverview/OrganizationThemeSelector';
import StatsSummary from '../../Components/AuditOverview/StatsSummary';
import OrgIcon from "../../icons/OverviewOrgIcon.svg";
import ThemeIcon from "../../icons/OverviewThemeIcon.svg";
import AuditsIcon from "../../icons/OverviewAuditsIcon.svg";
import ComplaintIcon from "../../icons/OverviewCompliantIcon.svg";
import NonComplaintIcon from "../../icons/OverviewNonComplaintIcon.svg";
import VisNetworkDiagram from '../../Components/AuditOverview/VisNetworkDiagram';
import NodeDetailModal from '../../Components/AuditOverview/NodeDetailModal';
import Breadcrumb from '../../Components/Breadcrumb';

type ViewMode = 'organization' | 'theme';
type TimePeriod = '1year' | '2year' | 'custom';

interface AuditOverviewGraphContainerProps {
    viewMode: ViewMode;
    selectedOrganization: string;
    selectedTheme: string;
    timePeriod: TimePeriod;
    customDateRange: { start: Date | null; end: Date | null };
    isLoading: boolean;
    organizationsList: any[];
    themesList: any[];
    isModalOpen: boolean;
    selectedNodeData: any;
    nodeToExpand: any;
    resetExpandedNodes: boolean;
    networkData: any;
    statsData: {
        organizations: number;
        themes: number;
        audits: number;
        compliant: number;
        nonCompliant: number;
    };
    onViewModeChange: (mode: ViewMode) => void;
    onOrganizationChange: (organizationId: string) => void;
    onThemeChange: (themeId: string) => void;
    onTimePeriodChange: (period: TimePeriod) => void;
    onCustomDateChange: (startDate: Date, endDate: Date) => void;
    onNodeClick: (nodeData: any) => void;
    onCloseModal: () => void;
    onNodeExpand: (node: any) => void;
}

const AuditOverviewGraphContainer: React.FC<AuditOverviewGraphContainerProps> = ({
    viewMode,
    selectedOrganization,
    selectedTheme,
    isLoading,
    organizationsList,
    themesList,
    isModalOpen,
    selectedNodeData,
    nodeToExpand,
    resetExpandedNodes,
    networkData,
    statsData,
    onViewModeChange,
    onOrganizationChange,
    onThemeChange,
    onTimePeriodChange,
    onCustomDateChange,
    onNodeClick,
    onCloseModal,
    onNodeExpand,
}) => {
    const breadcrumbItems = [
        { label: "Audit Overview", path: "/overview/audit-overview" },
    ];

    return (
        <PageContainer>
            <Breadcrumb items={breadcrumbItems} />
            <div className={styles.container}>
                {/* Filter Bar with Drilldown Capabilities */}
                <div className='mb-4'>
                    <OrganizationThemeSelector
                        organizations={organizationsList}
                        themes={themesList}
                        onViewModeChange={onViewModeChange}
                        onOrganizationChange={onOrganizationChange}
                        onThemeChange={onThemeChange}
                        onTimePeriodChange={onTimePeriodChange}
                        onCustomDateChange={onCustomDateChange}
                    />
                </div>

                {/* Loading Indicator */}
                {isLoading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}>Loading...</div>
                    </div>
                )}

                {/* Chart Section */}
                <div className={styles.chartSection}>
                    <StatsSummary
                        items={
                            viewMode === 'organization'
                                ? [
                                    {
                                        label: "Organization(s)",
                                        value: selectedOrganization || statsData.organizations,
                                        variant: "blue",
                                        iconSrc: OrgIcon,
                                    },
                                    {
                                        label: "Theme(s)",
                                        value: statsData.themes,
                                        variant: "purple",
                                        iconSrc: ThemeIcon,
                                    },
                                    {
                                        label: "Audit(s)",
                                        value: statsData.audits,
                                        variant: "cyan",
                                        iconSrc: AuditsIcon,
                                    },
                                    {
                                        label: "Compliant",
                                        value: statsData.compliant,
                                        variant: "green",
                                        iconSrc: ComplaintIcon,
                                    },
                                    {
                                        label: "Non-Compliant",
                                        value: statsData.nonCompliant,
                                        variant: "red",
                                        iconSrc: NonComplaintIcon,
                                    },
                                ]
                                : [
                                    {
                                        label: "Themes",
                                        value: selectedTheme || statsData.themes,
                                        variant: "purple",
                                        iconSrc: ThemeIcon,
                                    },
                                    {
                                        label: "Organizations",
                                        value: statsData.organizations,
                                        variant: "blue",
                                        iconSrc: OrgIcon,
                                    },
                                    {
                                        label: "Audits",
                                        value: statsData.audits,
                                        variant: "cyan",
                                        iconSrc: AuditsIcon,
                                    },
                                    {
                                        label: "Compliant",
                                        value: statsData.compliant,
                                        variant: "green",
                                        iconSrc: ComplaintIcon,
                                    },
                                    {
                                        label: "Non-Compliant",
                                        value: statsData.nonCompliant,
                                        variant: "red",
                                        iconSrc: NonComplaintIcon,
                                    },
                                ]
                        }
                    />
                    <div className='d-flex'>
                        {networkData ? (
                            networkData.network && networkData.network.length > 0 ? (
                                <VisNetworkDiagram
                                    data={networkData}
                                    onNodeClick={onNodeClick}
                                    nodeToExpand={nodeToExpand}
                                    resetExpandedNodes={resetExpandedNodes}
                                />
                            ) : (
                                <div className={styles.emptyState}>
                                    <svg width="80" height="80" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="60" cy="60" r="50" fill="#F3F4F6" />
                                        <path d="M45 55C45 52.2386 47.2386 50 50 50H70C72.7614 50 75 52.2386 75 55V75C75 77.7614 72.7614 80 70 80H50C47.2386 80 45 77.7614 45 75V55Z" stroke="#9CA3AF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M55 45V50M65 45V50M50 60H70M50 70H70" stroke="#9CA3AF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <h3 className={styles.emptyTitle}>No Data Found</h3>
                                    <p className={styles.emptyMessage}>
                                        Try selecting a different filter or date range to view audit data.
                                    </p>
                                </div>
                            )
                        ) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <p>Loading overview data...</p>
                            </div>
                        )}
                    </div>

                    {/* Node Detail Modal */}
                    <NodeDetailModal
                        isOpen={isModalOpen}
                        onClose={onCloseModal}
                        nodeData={selectedNodeData}
                        onNodeExpand={onNodeExpand}
                        viewMode={viewMode}
                    />
                </div>
            </div>
        </PageContainer>
    );
};

export default AuditOverviewGraphContainer;
